/**
 * Universal Chat Store - Single source of truth for all chat projections
 *
 * Unifies state across:
 * - AgenticChatBubble (collapsed/floating projection)
 * - Studio ChatInterface (embedded projection)
 *
 * See: docs/internal/gonzo/UNIVERSAL_CHAT_ARCHITECTURE.md
 */

import { create } from 'zustand'

// ---------------------------------------------------------------------------
// Canonical types
// ---------------------------------------------------------------------------

export type UniversalMode =
  | 'chat'
  | 'email'
  | 'terminal'
  | 'code'
  | 'dream'
  | 'qrg'
  | 'custom'

export type Projection = 'collapsed' | 'floating' | 'embedded' | 'maximized'

export interface ChatMessage {
  id: string
  role: 'user' | 'assistant' | 'system'
  content: string
  timestamp: number
  /** Agentic action attached to assistant messages */
  action?: AgenticAction
  /** UI command for instant preference changes */
  ui_command?: UiCommand
  /** Multi-step plan proposed by agent */
  plan?: AgentPlan
}

export interface AgenticAction {
  type: 'navigate' | 'highlight' | 'scroll'
  path?: string
  scrollTo?: string
  selector?: string
  message?: string
  arrow?: 'top' | 'bottom' | 'left' | 'right'
  target?: string
  highlight?: boolean
}

export interface UiCommand {
  type: string
  value?: unknown
  commands?: UiCommand[]
}

export interface AgentPlan {
  steps: Array<{ description: string; tool?: string; args?: Record<string, unknown> }>
  hash?: string
}

export interface CapabilityManifest {
  rag: boolean
  propose_actions: boolean
  execute_tools: boolean
  autopilot: boolean
  allowlist?: string[]
}

// Preferences (privacy-friendly, stored locally)
export type ThemeMode = 'system' | 'dark' | 'light'
export type TextSize = 'sm' | 'md' | 'lg'
export type AccentPreset = 'default' | 'pink' | 'blue' | 'emerald' | 'amber'
export type UiLanguage = 'en' | 'es' | 'fr' | 'de' | 'ch' | 'jp' | 'it'

export interface ChatPrefs {
  theme: ThemeMode
  textSize: TextSize
  accent: AccentPreset
  language: UiLanguage
  audioOnly: boolean
  autoSpeak: boolean
}

const DEFAULT_PREFS: ChatPrefs = {
  theme: 'system',
  textSize: 'md',
  accent: 'default',
  language: 'en',
  audioOnly: false,
  autoSpeak: false,
}

const PREFS_KEY = 'lukhas_chat_prefs'

function loadPrefs(): ChatPrefs {
  try {
    const raw = localStorage.getItem(PREFS_KEY)
    if (raw) return { ...DEFAULT_PREFS, ...JSON.parse(raw) }
  } catch { /* ignore */ }
  return { ...DEFAULT_PREFS }
}

function persistPrefs(prefs: ChatPrefs) {
  try {
    localStorage.setItem(PREFS_KEY, JSON.stringify(prefs))
  } catch { /* ignore */ }
}

// ---------------------------------------------------------------------------
// Store interface
// ---------------------------------------------------------------------------

interface UniversalChatState {
  // Messages (shared across all projections)
  messages: ChatMessage[]
  isStreaming: boolean
  currentStreamContent: string

  // Mode and projection
  mode: UniversalMode
  projection: Projection

  // Gate and capabilities
  gateOpen: boolean | null // null = not checked yet
  capabilities: CapabilityManifest | null

  // Preferences
  prefs: ChatPrefs

  // Input (shared so switching projections preserves draft)
  inputValue: string

  // Actions — messages
  addMessage: (msg: ChatMessage) => void
  appendStreamContent: (chunk: string) => void
  completeStream: (assistantContent: string, extras?: Partial<ChatMessage>) => void
  clearMessages: () => void
  setStreaming: (v: boolean) => void

  // Actions — mode/projection
  setMode: (mode: UniversalMode) => void
  setProjection: (projection: Projection) => void

  // Actions — gate/capabilities
  setGateOpen: (open: boolean) => void
  setCapabilities: (caps: CapabilityManifest) => void

  // Actions — preferences
  updatePrefs: (partial: Partial<ChatPrefs>) => void

  // Actions — input
  setInputValue: (v: string) => void
}

// ---------------------------------------------------------------------------
// Store
// ---------------------------------------------------------------------------

export const useUniversalChatStore = create<UniversalChatState>((set, get) => ({
  // Initial state
  messages: [],
  isStreaming: false,
  currentStreamContent: '',

  mode: 'chat',
  projection: 'collapsed',

  gateOpen: null,
  capabilities: null,

  prefs: loadPrefs(),

  inputValue: '',

  // --- Messages ---

  addMessage: (msg) => {
    set((s) => ({ messages: [...s.messages, msg] }))
  },

  appendStreamContent: (chunk) => {
    set((s) => ({ currentStreamContent: s.currentStreamContent + chunk }))
  },

  completeStream: (assistantContent, extras) => {
    const msg: ChatMessage = {
      id: `msg-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
      role: 'assistant',
      content: assistantContent,
      timestamp: Date.now(),
      ...extras,
    }
    set((s) => ({
      messages: [...s.messages, msg],
      isStreaming: false,
      currentStreamContent: '',
    }))
  },

  clearMessages: () => {
    set({ messages: [], currentStreamContent: '', isStreaming: false })
  },

  setStreaming: (v) => {
    set({ isStreaming: v, ...(v ? { currentStreamContent: '' } : {}) })
  },

  // --- Mode / Projection ---

  setMode: (mode) => set({ mode }),

  setProjection: (projection) => set({ projection }),

  // --- Gate / Capabilities ---

  setGateOpen: (open) => set({ gateOpen: open }),

  setCapabilities: (caps) => set({ capabilities: caps }),

  // --- Preferences ---

  updatePrefs: (partial) => {
    const next = { ...get().prefs, ...partial }
    persistPrefs(next)
    set({ prefs: next })
  },

  // --- Input ---

  setInputValue: (v) => set({ inputValue: v }),
}))
